import {
  INSIDE_GRID_SPOTS,
  INSIDE_ZERO_SPOTS,
  TABLE_ROWS,
  WHEEL_ORDER,
  WHEEL_POCKETS,
  betLabel,
  betSpotKey,
  colorOf,
  neighborsOf,
  playerColor,
  type InsideGridSpot,
  type InsideZeroSpot,
  type RlCallKind,
  type RlPlaceKind,
  type RlPublicBet,
} from "@ziolo/shared";
import { useMemo, useState, type CSSProperties } from "react";

const OUTSIDE: Array<{ kind: RlPlaceKind; label: string; fill?: "red" | "black" }> = [
  { kind: "LOW", label: "1 TO 18" },
  { kind: "EVEN", label: "EVEN" },
  { kind: "RED", label: "RED", fill: "red" },
  { kind: "BLACK", label: "BLACK", fill: "black" },
  { kind: "ODD", label: "ODD" },
  { kind: "HIGH", label: "19 TO 36" },
];

const DOZENS: Array<{ kind: RlPlaceKind; label: string }> = [
  { kind: "DOZEN_FIRST", label: "1ST 12" },
  { kind: "DOZEN_SECOND", label: "2ND 12" },
  { kind: "DOZEN_THIRD", label: "3RD 12" },
];

const COLUMNS: Array<{ kind: RlPlaceKind; label: string }> = [
  { kind: "COLUMN_THIRD", label: "2 TO 1" },
  { kind: "COLUMN_SECOND", label: "2 TO 1" },
  { kind: "COLUMN_FIRST", label: "2 TO 1" },
];

const PISTE_CALLS: Array<{ kind: RlCallKind; label: string; extra: string }> = [
  { kind: "TIERS_DU_CYLINDRE", label: "SERIE 5/8", extra: "rl-piste__call--tiers" },
  { kind: "ORPHELINS", label: "ORPHELINS", extra: "rl-piste__call--orph" },
  { kind: "VOISINS_DU_ZERO", label: "SERIE 0/2/3", extra: "rl-piste__call--voisins" },
  { kind: "JEU_ZERO", label: "ZERO\nSPIEL", extra: "rl-piste__call--jeu" },
];

const PISTE = {
  w: 1000,
  h: 208,
  pad: 4,
  ring: 38,
} as const;

const PISTE_FILL = {
  red: "#e53935",
  black: "#111111",
  green: "#2e7d32",
} as const;

const PISTE_COLS = [1.4, 0.95, 1.35, 0.62] as const;

interface ChipStack {
  amount: number;
  color: string;
  userId: number;
}

function chipsBySpot(bets: readonly RlPublicBet[]): Map<string, ChipStack[]> {
  const spots = new Map<string, ChipStack[]>();
  for (const bet of bets) {
    const key = betSpotKey(bet);
    const stacks = spots.get(key) ?? [];
    const existing = stacks.find((stack) => stack.userId === bet.userId);
    if (existing) {
      existing.amount += bet.amount;
    } else {
      stacks.push({ amount: bet.amount, color: playerColor(bet.userId), userId: bet.userId });
    }
    spots.set(key, stacks);
  }
  return spots;
}

function ChipPile({ stacks }: { stacks: ChipStack[] | undefined }) {
  if (!stacks || stacks.length === 0) return null;
  return (
    <span className="rl-chip-pile">
      {stacks.map((stack) => {
        const label = stack.amount >= 1000 ? `${Math.floor(stack.amount / 1000)}k` : String(stack.amount);
        return (
          <span
            key={stack.userId}
            className={`rl-chip${stack.amount >= 500 ? " rl-chip--lg" : stack.amount < 50 ? " rl-chip--sm" : ""}`}
            style={{ borderColor: stack.color }}
          >
            {label}
          </span>
        );
      })}
    </span>
  );
}

function Pip({ n }: { n: number }) {
  return (
    <span className={`rl-pip rl-pip--${colorOf(n)}${n === 0 ? " rl-pip--zero" : ""}`}>
      <span className="rl-pip__n">{n}</span>
    </span>
  );
}

function spotTitle(kind: RlPlaceKind, numbers: readonly number[]): string {
  return `${betLabel(kind, numbers)} · ${numbers.join("-")}`;
}

function stadiumD(x: number, y: number, w: number, h: number): string {
  const r = h / 2;
  return `M ${x + r} ${y} H ${x + w - r} A ${r} ${r} 0 0 1 ${x + w - r} ${y + h} H ${x + r} A ${r} ${r} 0 0 1 ${x + r} ${y} Z`;
}

/** Clockwise around a horizontal stadium, t in [0,1), 0 at the right tip. */
function capsulePoint(t: number, w: number, h: number): { x: number; y: number; nx: number; ny: number } {
  const u = ((t % 1) + 1) % 1;
  const r = h / 2;
  const side = Math.max(0, w - 2 * r);
  const arc = Math.PI * r;
  const quarter = arc / 2;
  const perimeter = 2 * side + 2 * arc;
  let d = u * perimeter;

  if (d <= quarter) {
    const theta = (d / quarter) * (Math.PI / 2);
    return { x: w - r + r * Math.cos(theta), y: r + r * Math.sin(theta), nx: Math.cos(theta), ny: Math.sin(theta) };
  }
  d -= quarter;
  if (d <= side) {
    return { x: w - r - d, y: h, nx: 0, ny: 1 };
  }
  d -= side;
  if (d <= arc) {
    const theta = Math.PI / 2 + (d / arc) * Math.PI;
    return { x: r + r * Math.cos(theta), y: r + r * Math.sin(theta), nx: Math.cos(theta), ny: Math.sin(theta) };
  }
  d -= arc;
  if (d <= side) {
    return { x: r + d, y: 0, nx: 0, ny: -1 };
  }
  d -= side;
  const theta = (3 * Math.PI) / 2 + (d / quarter) * (Math.PI / 2);
  return { x: w - r + r * Math.cos(theta), y: r + r * Math.sin(theta), nx: Math.cos(theta), ny: Math.sin(theta) };
}

function ringPoint(t: number, inset: number): { x: number; y: number } {
  const w = PISTE.w - 2 * PISTE.pad;
  const h = PISTE.h - 2 * PISTE.pad;
  const p = capsulePoint(t, w, h);
  return {
    x: PISTE.pad + p.x - p.nx * inset,
    y: PISTE.pad + p.y - p.ny * inset,
  };
}

function segmentPath(index: number): string {
  const t0 = index / WHEEL_POCKETS - 0.0008;
  const t1 = (index + 1) / WHEEL_POCKETS + 0.0008;
  const steps = 10;
  const outer: string[] = [];
  const inner: string[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = t0 + ((t1 - t0) * i) / steps;
    const o = ringPoint(t, 0);
    const n = ringPoint(t, PISTE.ring);
    outer.push(`${o.x.toFixed(2)} ${o.y.toFixed(2)}`);
    inner.push(`${n.x.toFixed(2)} ${n.y.toFixed(2)}`);
  }
  inner.reverse();
  return `M ${outer.join(" L ")} L ${inner.join(" L ")} Z`;
}

function pisteMidStyle(index: number): CSSProperties {
  const mid = ringPoint((index + 0.5) / WHEEL_POCKETS, PISTE.ring / 2);
  return {
    left: `${(mid.x / PISTE.w) * 100}%`,
    top: `${(mid.y / PISTE.h) * 100}%`,
  };
}

function buildPisteLayout() {
  const innerX = PISTE.pad + PISTE.ring;
  const innerY = PISTE.pad + PISTE.ring;
  const innerW = PISTE.w - 2 * (PISTE.pad + PISTE.ring);
  const innerH = PISTE.h - 2 * (PISTE.pad + PISTE.ring);
  const total = PISTE_COLS.reduce((sum, col) => sum + col, 0);
  const dividers: number[] = [];
  let x = innerX;
  for (let i = 0; i < PISTE_COLS.length - 1; i += 1) {
    x += (innerW * PISTE_COLS[i]!) / total;
    dividers.push(x);
  }
  const orphLeft = dividers[0] ?? innerX;
  const orphRight = dividers[1] ?? innerX + innerW;
  return {
    outerD: stadiumD(PISTE.pad, PISTE.pad, PISTE.w - 2 * PISTE.pad, PISTE.h - 2 * PISTE.pad),
    innerD: stadiumD(innerX, innerY, innerW, innerH),
    innerY,
    innerH,
    dividers,
    orphLeft,
    orphRight,
    segments: WHEEL_ORDER.map((n, index) => ({
      n,
      index,
      d: segmentPath(index),
      label: ringPoint((index + 0.5) / WHEEL_POCKETS, PISTE.ring / 2),
      fill: PISTE_FILL[colorOf(n)],
    })),
  };
}

export function RuletkaBoard({
  bets,
  winningNumber,
  disabled,
  trackMode,
  onPlace,
}: {
  bets: readonly RlPublicBet[];
  winningNumber: number | null;
  disabled: boolean;
  trackMode: "straight" | "neighbors";
  onPlace: (kind: RlPlaceKind, numbers?: number[]) => void;
}) {
  const stacks = chipsBySpot(bets);
  const [hoverNeighbors, setHoverNeighbors] = useState<number[]>([]);
  const piste = useMemo(buildPisteLayout, []);

  function onTrack(n: number) {
    if (trackMode === "neighbors") onPlace("NEIGHBORS", [n]);
    else onPlace("STRAIGHT_UP", [n]);
  }

  return (
    <div className={`rl-board${disabled ? " rl-board--closed" : ""}`}>
      <div className="rl-table">
        <span className="rl-table__pad rl-table__pad--zero" aria-hidden />
        <span className="rl-table__pad rl-table__pad--col" aria-hidden />
        <div className="rl-zero-wrap">
          <div className="rl-zero-shape">
            <button
              type="button"
              className={`rl-cell rl-zero${winningNumber === 0 ? " rl-cell--hit" : ""}`}
              disabled={disabled}
              onClick={() => onPlace("STRAIGHT_UP", [0])}
            >
              <Pip n={0} />
              <ChipPile stacks={stacks.get("n:0")} />
            </button>
          </div>
          <span className="rl-zero-line">
            {INSIDE_ZERO_SPOTS.map((spot) => (
              <ZeroSpotButton
                key={spotTitle(spot.kind, spot.numbers)}
                spot={spot}
                disabled={disabled}
                stacks={stacks.get(betSpotKey(spot))}
                onPlace={onPlace}
              />
            ))}
          </span>
        </div>

        <div className="rl-nums">
          {TABLE_ROWS.flatMap((row) =>
            row.map((n) => (
              <button
                key={n}
                type="button"
                className={`rl-cell rl-num${winningNumber === n ? " rl-cell--hit" : ""}`}
                disabled={disabled}
                onClick={() => onPlace("STRAIGHT_UP", [n])}
              >
                <Pip n={n} />
                <ChipPile stacks={stacks.get(`n:${n}`)} />
              </button>
            )),
          )}
          <div className="rl-inside" aria-hidden={disabled}>
            {INSIDE_GRID_SPOTS.map((spot) => (
              <GridSpotButton
                key={`${spot.shape}-${spot.col}-${spot.row}-${spot.kind}`}
                spot={spot}
                disabled={disabled}
                stacks={stacks.get(betSpotKey(spot))}
                onPlace={onPlace}
              />
            ))}
          </div>
        </div>

        <div className="rl-col21">
          {COLUMNS.map((column) => (
            <button
              key={column.kind}
              type="button"
              className="rl-cell rl-col21-cell"
              disabled={disabled}
              onClick={() => onPlace(column.kind)}
            >
              <span className="rl-col21-cell__label">{column.label}</span>
              <ChipPile stacks={stacks.get(`bet:${column.kind}`)} />
            </button>
          ))}
        </div>

        <div className="rl-doz">
          {DOZENS.map((dozen) => (
            <button
              key={dozen.kind}
              type="button"
              className="rl-cell rl-doz-cell"
              disabled={disabled}
              onClick={() => onPlace(dozen.kind)}
            >
              {dozen.label}
              <ChipPile stacks={stacks.get(`bet:${dozen.kind}`)} />
            </button>
          ))}
        </div>

        <div className="rl-out">
          {OUTSIDE.map((item) => (
            <button
              key={item.kind}
              type="button"
              className={`rl-cell rl-out-cell${item.fill ? ` rl-out-cell--${item.fill}` : ""}`}
              disabled={disabled}
              onClick={() => onPlace(item.kind)}
            >
              {item.label}
              <ChipPile stacks={stacks.get(`bet:${item.kind}`)} />
            </button>
          ))}
        </div>
      </div>

      <div className="rl-piste" aria-label="Tory">
        <svg className="rl-piste__svg" viewBox={`0 0 ${PISTE.w} ${PISTE.h}`}>
          <defs>
            <clipPath id="rl-piste-inner">
              <path d={piste.innerD} />
            </clipPath>
          </defs>
          <path d={piste.outerD} fill="#2e7d32" stroke="#fff" strokeWidth="3" />
          {piste.segments.map((seg) => {
            const hot = hoverNeighbors.includes(seg.n);
            const won = winningNumber === seg.n;
            return (
              <g key={seg.n}>
                <path
                  className={`rl-piste__seg${hot ? " rl-piste__seg--hot" : ""}${won ? " rl-piste__seg--hit" : ""}`}
                  d={seg.d}
                  fill={seg.fill}
                  stroke="#fff"
                  strokeWidth="1.4"
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  onClick={() => {
                    if (!disabled) onTrack(seg.n);
                  }}
                  onKeyDown={(event) => {
                    if (!disabled && (event.key === "Enter" || event.key === " ")) {
                      event.preventDefault();
                      onTrack(seg.n);
                    }
                  }}
                  onMouseEnter={() => {
                    if (trackMode === "neighbors") setHoverNeighbors(neighborsOf(seg.n));
                  }}
                  onMouseLeave={() => setHoverNeighbors([])}
                >
                  <title>{spotTitle(trackMode === "neighbors" ? "NEIGHBORS" : "STRAIGHT_UP", [seg.n])}</title>
                </path>
                <text
                  x={seg.label.x}
                  y={seg.label.y}
                  className="rl-piste__label"
                  fill="#fff"
                  textAnchor="middle"
                  dominantBaseline="central"
                  pointerEvents="none"
                >
                  {seg.n}
                </text>
              </g>
            );
          })}
          <path d={piste.innerD} fill="#2e7d32" stroke="#fff" strokeWidth="2.5" pointerEvents="none" />
          <g clipPath="url(#rl-piste-inner)" pointerEvents="none">
            {piste.dividers.map((x) => (
              <line
                key={x}
                x1={x}
                y1={piste.innerY}
                x2={x}
                y2={piste.innerY + piste.innerH}
                stroke="#fff"
                strokeWidth="2"
              />
            ))}
            <line
              x1={piste.orphLeft}
              y1={piste.innerY}
              x2={piste.orphRight}
              y2={piste.innerY + piste.innerH}
              stroke="#fff"
              strokeWidth="2"
            />
            <line
              x1={piste.orphLeft}
              y1={piste.innerY + piste.innerH}
              x2={piste.orphRight}
              y2={piste.innerY}
              stroke="#fff"
              strokeWidth="2"
            />
          </g>
        </svg>
        <div className="rl-piste__core">
          {PISTE_CALLS.map((call) => (
            <button
              key={call.extra}
              type="button"
              className={`rl-piste__call ${call.extra}`}
              disabled={disabled}
              onClick={() => onPlace(call.kind)}
            >
              {call.label}
              <ChipPile stacks={stacks.get(`call:${call.kind}`)} />
            </button>
          ))}
        </div>
        {WHEEL_ORDER.map((n, index) => (
          <span key={`chip-${n}`} className="rl-piste__chips" style={pisteMidStyle(index)}>
            <ChipPile stacks={stacks.get(`n:${n}`)} />
          </span>
        ))}
      </div>
    </div>
  );
}

function GridSpotButton({
  spot,
  disabled,
  stacks,
  onPlace,
}: {
  spot: InsideGridSpot;
  disabled: boolean;
  stacks: ChipStack[] | undefined;
  onPlace: (kind: RlPlaceKind, numbers?: number[]) => void;
}) {
  return (
    <button
      type="button"
      className={`rl-inside__spot rl-inside__spot--${spot.shape}`}
      style={{ "--c": spot.col, "--r": spot.row } as CSSProperties}
      disabled={disabled}
      title={spotTitle(spot.kind, spot.numbers)}
      aria-label={spotTitle(spot.kind, spot.numbers)}
      onClick={(event) => {
        event.stopPropagation();
        onPlace(spot.kind, spot.numbers);
      }}
    >
      <ChipPile stacks={stacks} />
    </button>
  );
}

function ZeroSpotButton({
  spot,
  disabled,
  stacks,
  onPlace,
}: {
  spot: InsideZeroSpot;
  disabled: boolean;
  stacks: ChipStack[] | undefined;
  onPlace: (kind: RlPlaceKind, numbers?: number[]) => void;
}) {
  return (
    <button
      type="button"
      className="rl-inside__spot rl-inside__spot--zero"
      disabled={disabled}
      title={spotTitle(spot.kind, spot.numbers)}
      aria-label={spotTitle(spot.kind, spot.numbers)}
      onClick={(event) => {
        event.stopPropagation();
        onPlace(spot.kind, spot.numbers);
      }}
    >
      <ChipPile stacks={stacks} />
    </button>
  );
}
